import { UserProfileRole, UserStatus } from '@@types/api.types';
import { CompanyProfileModal } from '@components/pages/company/components/company-company-profile-modal.component';
import { ExpertProfileModal } from '@components/pages/expert-profile/components/expert-profile-modal.component';
import { TechnologyTechnologyModal } from '@components/pages/technology/components/technology-technology-modal.component';
import { EmptyStateContainerProps } from '@components/shared/empty-state-container.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { successToast } from '@hooks/toast.hook';
import { useTechnologiesQueryOptions } from '@queries/technology.query';
import { useGetCurrentUser, useResendVerificationEmail } from '@queries/users.query';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { UserProfileModal } from './home-user-profile-modal.component';

export const HomeOnboardingTile: React.FC = () => {
  const { data: user, isLoading: isUserLoading } = useGetCurrentUser();
  const technologiesQueryOptions = useTechnologiesQueryOptions();
  const { data: technologies, isLoading: isTechnologiesLoading } = useQuery(technologiesQueryOptions);

  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isTechnologyModalOpen, setIsTechnologyModalOpen] = useState(false);
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState(false);
  const [isExpertProfileModalOpen, setIsExpertProfileModalOpen] = useState(false);

  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const { isPending: isResendPending, mutateAsync: resendVerificationEmail } = useResendVerificationEmail();

  const handleResendEmail = async () => {
    try {
      await resendVerificationEmail();
      successToast({
        description: 'Verification email sent! Please check your inbox.',
        title: 'Email Sent',
      });
      setIsResendDisabled(true);
      setRemainingSeconds(30);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
    }
  };

  useEffect(() => {
    if (remainingSeconds > 0) {
      const timer = setTimeout(() => {
        setRemainingSeconds(remainingSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (remainingSeconds === 0 && isResendDisabled) {
      setIsResendDisabled(false);
    }
  }, [isResendDisabled, remainingSeconds]);

  const doNotHaveUserProfile = !user?.profile;
  const doNotHaveExpertProfile = !user?.expertProfile;
  const doNotHaveCompany = !user?.companies || user.companies.length === 0;
  const doNotHaveTechnologies = !technologies || technologies.length === 0;

  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;
  const userHasValidAccess = Boolean(user?.hasValidAccess);

  const emptyStateProps: EmptyStateContainerProps | undefined = (() => {
    if (!user?.emailVerified) {
      return {
        message: (
          <div className="flex flex-col items-center gap-2">
            <p className="text-sm text-gray-600 max-w-lg text-center">
              A verification email has been sent to{' '}
              {user?.email ? <span className="font-medium">{user.email}</span> : 'you'}.<br />
              Please check your inbox to proceed.
            </p>
            <div className="pt-2 text-center flex flex-col justify-center items-center">
              <span className="text-sm text-gray-600">Didn't receive the email? </span>
              <Button
                disabled={isResendDisabled || isResendPending}
                onClick={handleResendEmail}
                variant="link"
              >
                {isResendDisabled
                  ? `Resend Email (${remainingSeconds}s)`
                  : isResendPending
                    ? 'Sending...'
                    : 'Resend email'}
              </Button>
            </div>
          </div>
        ),
        title: 'Email Pending Verification',
      };
    }

    if (user?.status === UserStatus.BLOCKED || user?.hasAccessExpired) {
      return {
        message: (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-gray-600 max-w-lg text-center">
              Please contact{' '}
              <ExternalLink
                className="text-blue-600 hover:text-blue-800 cursor-pointer"
                href="mailto:alok@azymmetric.com"
                showIcon={false}
              >
                alok@azymmetric.com
              </ExternalLink>{' '}
              for assistance.
            </p>
          </div>
        ),
        title: 'Access Restricted',
      };
    }

    if (doNotHaveUserProfile) {
      return {
        message: 'Add your profile now',
        onClick: () => setIsUserProfileModalOpen(true),
        title: 'User Profile Missing',
      };
    }

    if (isExpert && doNotHaveExpertProfile) {
      return {
        message: 'Create your Expert Profile now',
        onClick: () => setIsExpertProfileModalOpen(true),
        title: 'Expert Profile Missing',
      };
    }

    if (user?.status === UserStatus.PENDING) {
      return {
        message: (
          <p className="text-sm text-gray-600 max-w-lg text-center">
            Contact{' '}
            <ExternalLink
              className="text-blue-600 hover:text-blue-800 cursor-pointer"
              href="mailto:alok@azymmetric.com"
              showIcon={false}
            >
              alok@azymmetric.com
            </ExternalLink>{' '}
            if you need help
          </p>
        ),
        title: 'Account Pending Approval',
      };
    }

    if (userHasValidAccess && isInnovator && doNotHaveCompany) {
      return {
        message: 'Add your Company now',
        onClick: () => setIsCompanyModalOpen(true),
        title: 'Company Missing',
      };
    }

    return undefined;
  })();

  const showTile = emptyStateProps && !isUserLoading && !isTechnologiesLoading;

  return (
    <>
      {showTile && (
        <Tile
          className="min-h-[160px]"
          isEmpty={true}
          emptyState={emptyStateProps}
        />
      )}
      <UserProfileModal
        isOpen={isUserProfileModalOpen}
        onClose={() => setIsUserProfileModalOpen(false)}
      />
      <CompanyProfileModal
        isOpen={isCompanyModalOpen}
        onClose={() => setIsCompanyModalOpen(false)}
        onContinue={() => setIsTechnologyModalOpen(true)}
        onCompanyCreated={() => {}}
      />
      <TechnologyTechnologyModal
        isEdit={false}
        isOpen={isTechnologyModalOpen}
        onClose={() => setIsTechnologyModalOpen(false)}
      />

      <ExpertProfileModal
        isOpen={isExpertProfileModalOpen}
        onClose={() => setIsExpertProfileModalOpen(false)}
        expertProfile={user?.expertProfile}
        isEdit={!!user?.expertProfile}
      />
    </>
  );
};
